import React from 'react';
import { formatBytes, gbToBytes } from '../lib/bytes';
import { fillTemplate } from '../lib/selection';
import { storageProjection } from '../lib/usage';
import UsageBar from './UsageBar';

interface StorageUsageBarBaseProps {
    /** Bytes currently used. */
    usedBytes: number;
    /** Effective tier cap in GB. ``null`` or ``<= 0`` → unlimited (no fill). */
    maxGb: number | null;
    /** Trailing "used / total" label. Default `true`. */
    showLabel?: boolean;
    /** Label shown for the cap when unlimited. Default `"∞"`. */
    unlimitedLabel?: string;
    /** Track thickness: `sm` (h-2, admin rows) | `md` (h-3, default). */
    size?: 'sm' | 'md';
    /** Wrapper classes (e.g. `flex-1` inside an admin row). */
    className?: string;
}

/**
 * Pending bytes come WITH the copy for the over-cap case: a bar that can go
 * over the cap has to be able to say by how much, and the component does not
 * own any language. Both or neither — the type enforces it.
 */
type StorageUsageBarPending =
    | { pendingBytes?: undefined; overLabel?: undefined }
    | {
          /** Bytes not committed yet (what is about to be uploaded), drawn
           *  hatched after the used part and added to the label as `+ X`. */
          pendingBytes: number;
          /** Over-cap suffix, with `{amount}` («supera el límite en {amount}»). */
          overLabel: string;
      };

export type StorageUsageBarProps = StorageUsageBarBaseProps & StorageUsageBarPending;

/**
 * Storage usage track shared by Admin (per-tenant rows) and Tenant
 * (Settings → Taller). A coloured fill scales with `used / max` using the
 * coherent storage thresholds (same as the tenant banner + warning email):
 *   < 75% emerald · 75–89% amber (warning) · 90–99% orange (urgent) ·
 *   100% red (full → new uploads blocked, HTTP 507).
 * When `maxGb` is null/≤0 the cap is unlimited: no fill, label shows
 * `used · ∞`. Pure presentational — callers supply the bytes.
 *
 * With `pendingBytes` (the migration wizard's live «how would my storage end
 * up» bar) the projected part is drawn hatched in the colour of the projected
 * total, the label reads `used + pending / cap`, and past the cap the hatch
 * turns red (always visible, even on a bar that is already full) and the
 * label appends `overLabel` with the excess. The numbers are
 * `lib/usage.ts::storageProjection`, pinned by a Node test.
 */
const StorageUsageBar: React.FC<StorageUsageBarProps> = ({
    usedBytes,
    maxGb,
    showLabel = true,
    unlimitedLabel = '∞',
    size = 'md',
    className = '',
    pendingBytes,
    overLabel,
}) => {
    const used = Math.max(0, usedBytes || 0);
    const pendingRaw = Math.max(0, pendingBytes || 0);
    const capBytes = maxGb == null || maxGb <= 0 ? null : gbToBytes(maxGb);
    const { unlimited, pct, pendingPct, level, projectedLevel, excess, over } =
        storageProjection(used, capBytes, pendingRaw);

    const usedText = pendingRaw > 0 ? `${formatBytes(used)} + ${formatBytes(pendingRaw)}` : formatBytes(used);
    const capText = unlimited ? unlimitedLabel : `${maxGb} GB`;
    const overText = over && overLabel ? fillTemplate(overLabel, { amount: formatBytes(excess) }) : null;
    const label = `${usedText} / ${capText}${overText ? ` · ${overText}` : ''}`;

    return (
        <div className={`flex items-center gap-3 ${className}`}>
            <UsageBar
                pct={unlimited ? null : pct}
                level={level}
                pendingPct={pendingRaw > 0 ? pendingPct : undefined}
                pendingLevel={projectedLevel}
                // The bar follows the BYTE decision the label uses, so the two
                // never disagree at exactly the cap (float shares).
                overCap={over}
                size={size}
                valueText={label}
                className="flex-1"
            />
            {showLabel && (
                <span
                    className={`text-xs whitespace-nowrap shrink-0 ${
                        over ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-text-sub dark:text-slate-400'
                    }`}
                >
                    {usedText} / {capText}
                    {overText && <> · {overText}</>}
                </span>
            )}
        </div>
    );
};

export default StorageUsageBar;
